import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, DownloadSimple, FileArrowUp, UploadSimple, MagicWand, PencilSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { AdminHeader, Field, inputCls, PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, API, getErrorMessage, ADMIN_TOKEN_KEY } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DOC_TYPES = [
  { value: "manual", label: "User Manual" },
  { value: "wiring_diagram", label: "Wiring Diagram" },
  { value: "technical", label: "Technical Document" },
  { value: "troubleshooting", label: "Troubleshooting Guide" },
  { value: "spec", label: "Specification" },
  { value: "service_bulletin", label: "Service Bulletin" },
  { value: "warranty", label: "Warranty" },
  { value: "training", label: "Training Material" },
  { value: "other", label: "Other" },
];

const VISIBILITY = [
  { value: "customer", label: "Customer" },
  { value: "ai_internal", label: "AI Internal" },
  { value: "admin_only", label: "Admin Only" },
];

function ModeBtn({ active, children, ...props }) {
  return (
    <button
      type="button"
      className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
        active
          ? "bg-[#73bf44] text-[#050505] border-[#73bf44]"
          : "bg-transparent text-neutral-400 border-neutral-700 hover:border-neutral-500"
      }`}
      {...props}
    >
      {children}
    </button>
  );
}

function ModelAssign({ models, all, setAll, selected, setSelected, idp = "" }) {
  const [search, setSearch] = useState("");
  const filtered = models.filter((m) => m.toLowerCase().includes(search.trim().toLowerCase()));
  const toggle = (m) => setSelected(selected.includes(m) ? selected.filter((x) => x !== m) : [...selected, m]);
  const selectAllShown = () => setSelected(Array.from(new Set([...selected, ...filtered])));

  return (
    <div>
      <div className="flex gap-2 mb-2.5">
        <ModeBtn active={all} onClick={() => setAll(true)} data-testid={`${idp}model-mode-all`}>All models</ModeBtn>
        <ModeBtn active={!all} onClick={() => setAll(false)} data-testid={`${idp}model-mode-specific`}>Specific models</ModeBtn>
      </div>
      {all ? (
        <p className="text-xs text-neutral-500 px-1">This document will be shown for every caravan model.</p>
      ) : (
        <>
          <div className="relative mb-2">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-600" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search models…"
              className={`${inputCls} pl-8`}
              data-testid={`${idp}model-assign-search`}
            />
          </div>
          <div className="flex items-center justify-between mb-2 text-xs">
            <button type="button" onClick={selectAllShown} className="text-[#73bf44] hover:underline font-semibold" data-testid={`${idp}model-assign-select-all`}>
              Select all shown ({filtered.length})
            </button>
            <div className="flex items-center gap-3">
              <span className="text-neutral-400 font-semibold" data-testid={`${idp}model-assign-count`}>{selected.length} selected</span>
              {selected.length > 0 && (
                <button type="button" onClick={() => setSelected([])} className="text-neutral-500 hover:text-white">Clear</button>
              )}
            </div>
          </div>
          <div className="max-h-52 overflow-y-auto border border-neutral-800 rounded-lg divide-y divide-neutral-900 bg-black/30" data-testid={`${idp}model-assign-list`}>
            {filtered.length === 0 ? (
              <div className="text-neutral-600 text-sm py-6 text-center">No models match.</div>
            ) : (
              filtered.map((m) => (
                <label key={m} className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 cursor-pointer text-sm text-neutral-200">
                  <input
                    type="checkbox"
                    checked={selected.includes(m)}
                    onChange={() => toggle(m)}
                    className="accent-[#73bf44] w-4 h-4 shrink-0"
                    data-testid={`${idp}model-assign-checkbox`}
                  />
                  <span className="truncate">{m}</span>
                </label>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function DocumentManager({ title, subtitle, fixedType }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allModels, setAllModels] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [retagging, setRetagging] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState(defaults(fixedType));

  // Edit dialog
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editAll, setEditAll] = useState(true);
  const [editModels, setEditModels] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  function defaults(t) {
    const customerFacing = t === "manual" || t === "service_bulletin";
    return {
      title: "", doc_type: t || "technical", category: "",
      visibility: customerFacing ? "customer" : "ai_internal",
      ai_enabled: true, customer_enabled: customerFacing,
      status: "approved", appliesAll: true, appliesModels: [], applies_to_year: "", equipment: "", file: null,
    };
  }

  const load = () => {
    setLoading(true);
    const params = fixedType ? { doc_type: fixedType } : {};
    adminApi.get("/admin/documents", { params }).then(({ data }) => setRows(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fixedType]);
  useEffect(() => { adminApi.get("/admin/model-list").then(({ data }) => setAllModels(data)).catch(() => {}); }, []);

  useEffect(() => {
    const busy = rows.some((r) => r.indexing_status === "processing" || r.indexing_status === "indexing");
    if (!busy) return undefined;
    const t = setTimeout(() => {
      const params = fixedType ? { doc_type: fixedType } : {};
      adminApi.get("/admin/documents", { params }).then(({ data }) => setRows(data)).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [rows, fixedType]);

  const openNew = () => { setForm(defaults(fixedType)); setOpen(true); };

  const submit = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    if (!form.file) { toast.error("Please choose a file to upload"); return; }
    if (!form.appliesAll && form.appliesModels.length === 0) { toast.error("Select at least one model, or choose “All models”"); return; }
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("doc_type", form.doc_type);
    fd.append("category", form.category);
    fd.append("visibility", form.visibility);
    fd.append("ai_enabled", form.ai_enabled);
    fd.append("customer_enabled", form.customer_enabled);
    fd.append("status", form.status);
    fd.append("applies_to_models", form.appliesAll ? "" : JSON.stringify(form.appliesModels));
    fd.append("applies_to_year", form.applies_to_year);
    fd.append("equipment", form.equipment);
    fd.append("file", form.file);
    setUploading(true);
    try {
      await adminApi.post("/admin/documents", fd);
      toast.success("Uploaded — indexing in the background");
      setOpen(false); load();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setUploading(false); }
  };

  const openEdit = (r) => {
    const models = (r.applies_to_models || []).filter((m) => m && m.toLowerCase() !== "all");
    setEditRow(r);
    setEditForm({
      title: r.title || "", category: r.category || "", doc_type: r.doc_type || "technical",
      visibility: r.visibility || (r.customer_enabled ? "customer" : "ai_internal"),
      status: r.status || "approved", ai_enabled: !!r.ai_enabled, customer_enabled: !!r.customer_enabled,
      equipment: r.equipment || "",
    });
    setEditAll(models.length === 0);
    setEditModels(models);
  };

  const saveEdit = async () => {
    if (!editAll && editModels.length === 0) { toast.error("Select at least one model, or choose “All models”"); return; }
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        applies_to_models: editAll ? [] : editModels,
      };
      const { data } = await adminApi.put(`/admin/documents/${editRow.id}`, payload);
      setRows((prev) => prev.map((x) => (x.id === editRow.id ? { ...x, ...data } : x)));
      toast.success("Saved");
      setEditRow(null);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSavingEdit(false); }
  };

  const toggle = async (r, key) => {
    try {
      await adminApi.put(`/admin/documents/${r.id}`, { [key]: !r[key] });
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, [key]: !x[key] } : x)));
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const setStatus = async (r, status) => {
    await adminApi.put(`/admin/documents/${r.id}`, { status });
    setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, status } : x)));
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    await adminApi.delete(`/admin/documents/${r.id}`);
    toast.success("Deleted"); load();
  };

  const downloadUrl = (r) => `${API}/documents/${r.id}/file?auth=${localStorage.getItem(ADMIN_TOKEN_KEY)}`;

  const modelsLabel = (r) => {
    const m = (r.applies_to_models || []).filter((x) => x && x.toLowerCase() !== "all");
    return m.length ? m.join(", ") : "All models";
  };

  const retagAll = async () => {
    setRetagging(true);
    try {
      const { data } = await adminApi.post("/admin/documents/recategorize");
      toast.success(
        data.queued
          ? `Auto-tagging ${data.queued} document${data.queued > 1 ? "s" : ""} in the background…`
          : "All documents are already tagged"
      );
      setTimeout(load, 3500);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setRetagging(false); }
  };

  return (
    <div>
      <AdminHeader
        title={title}
        subtitle={subtitle}
        actions={
          <div className="flex items-center gap-2">
            <GhostButton onClick={retagAll} disabled={retagging} data-testid="document-retag-button">
              <MagicWand size={16} /> {retagging ? "Tagging…" : "Auto-tag existing"}
            </GhostButton>
            <PrimaryButton onClick={openNew} data-testid="document-upload-button"><Plus size={16} /> Upload</PrimaryButton>
          </div>
        }
      />
      <div className="p-8">
        {loading ? <div className="text-neutral-600">Loading...</div> : rows.length === 0 ? (
          <div className="text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-16 text-center flex flex-col items-center gap-3">
            <FileArrowUp size={36} className="text-neutral-700" weight="duotone" />
            No documents yet. Click Upload to add a PDF.
          </div>
        ) : (
          <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[880px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                  <th className="px-5 py-3 font-medium">Document</th>
                  <th className="px-5 py-3 font-medium">Applies to</th>
                  <th className="px-5 py-3 font-medium">AI</th>
                  <th className="px-5 py-3 font-medium">Customer</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Indexed</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid="document-row" className="border-b border-neutral-800/60 last:border-0 hover:bg-white/[0.02]">
                    <td className="px-5 py-3.5">
                      <div className="text-neutral-100 font-medium">{r.title}</div>
                      <div className="text-xs text-neutral-600">{r.filename || "—"} · <span className="capitalize">{(r.doc_type || "").replace("_", " ")}</span></div>
                    </td>
                    <td className="px-5 py-3.5" data-testid="document-applies-to">
                      {modelsLabel(r) === "All models" ? (
                        <span className="text-[0.7rem] px-2 py-0.5 rounded-full bg-[#73bf44]/10 border border-[#73bf44]/25 text-[#73bf44] font-semibold">All models</span>
                      ) : (
                        <div className="flex flex-wrap gap-1 max-w-[220px]">
                          {(r.applies_to_models || []).filter((x) => x && x.toLowerCase() !== "all").slice(0, 3).map((m) => (
                            <span key={m} className="text-[0.7rem] px-2 py-0.5 rounded-full bg-black/50 border border-neutral-700 text-neutral-300">{m}</span>
                          ))}
                          {(r.applies_to_models || []).filter((x) => x && x.toLowerCase() !== "all").length > 3 && (
                            <span className="text-[0.7rem] px-2 py-0.5 rounded-full bg-neutral-800 text-neutral-400">+{(r.applies_to_models || []).filter((x) => x && x.toLowerCase() !== "all").length - 3}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3.5"><Toggle on={r.ai_enabled} onClick={() => toggle(r, "ai_enabled")} testid="toggle-ai" /></td>
                    <td className="px-5 py-3.5"><Toggle on={r.customer_enabled} onClick={() => toggle(r, "customer_enabled")} testid="toggle-customer" /></td>
                    <td className="px-5 py-3.5">
                      <select value={r.status} onChange={(e) => setStatus(r, e.target.value)} className="bg-transparent text-xs text-neutral-300 outline-none cursor-pointer">
                        <option value="draft" className="bg-neutral-900">Draft</option>
                        <option value="approved" className="bg-neutral-900">Approved</option>
                        <option value="archived" className="bg-neutral-900">Archived</option>
                      </select>
                    </td>
                    <td className="px-5 py-3.5 text-xs">
                      {r.indexing_status === "processing" || r.indexing_status === "indexing" ? (
                        <div className="w-36" data-testid="document-indexing">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-amber-400 text-[11px] truncate pr-2">{r.progress_stage || "Processing"}</span>
                            <span className="text-amber-400 text-[11px] font-semibold tabular-nums" data-testid="document-progress-pct">{r.progress ?? 0}%</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-neutral-800 overflow-hidden">
                            <div className="h-full bg-amber-400 rounded-full transition-all duration-500 ease-out" style={{ width: `${r.progress ?? 0}%` }} />
                          </div>
                        </div>
                      ) : r.indexing_status === "error" ? (
                        <span className="text-red-400">Failed</span>
                      ) : r.text_length > 0 ? (
                        <span className="text-neutral-500">{r.text_length.toLocaleString()} chars</span>
                      ) : (
                        <span className="text-neutral-500">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(r)} data-testid="document-edit" className="inline-flex p-2 rounded-lg text-neutral-400 hover:text-[#73bf44] hover:bg-[#73bf44]/10" title="Edit permissions & models"><PencilSimple size={16} /></button>
                      <a href={downloadUrl(r)} target="_blank" rel="noreferrer" data-testid="document-download" className="inline-flex p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/[0.05]"><DownloadSimple size={16} /></a>
                      <button onClick={() => del(r)} data-testid="document-delete" className="inline-flex p-2 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-red-500/10"><Trash size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-white">Upload {fixedType === "manual" ? "Manual" : fixedType === "service_bulletin" ? "Service Bulletin" : "Document"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Title"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="document-title" placeholder="e.g. Adventure Owner's Manual 2026" /></Field>
            <div className="grid grid-cols-2 gap-3">
              {!fixedType && (
                <Field label="Document type">
                  <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className={inputCls} data-testid="document-type">
                    {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Category"><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} placeholder="Electrical" /></Field>
              {fixedType && <Field label="Equipment"><input value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} className={inputCls} placeholder="Projecta PM335" /></Field>}
            </div>
            <Field label="Applies to models" hint="Choose which caravan models can see this document.">
              <ModelAssign
                models={allModels}
                all={form.appliesAll}
                setAll={(v) => setForm({ ...form, appliesAll: v })}
                selected={form.appliesModels}
                setSelected={(v) => setForm({ ...form, appliesModels: v })}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Visibility">
                <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} className={inputCls} data-testid="document-visibility">
                  {VISIBILITY.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </Field>
              <Field label="Model year"><input value={form.applies_to_year} onChange={(e) => setForm({ ...form, applies_to_year: e.target.value })} className={inputCls} placeholder="2026" /></Field>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.ai_enabled} onChange={(e) => setForm({ ...form, ai_enabled: e.target.checked })} className="accent-[#73bf44] w-4 h-4" data-testid="document-ai-enabled" />
                <span className="text-sm">AI can use</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.customer_enabled} onChange={(e) => setForm({ ...form, customer_enabled: e.target.checked })} className="accent-[#73bf44] w-4 h-4" data-testid="document-customer-enabled" />
                <span className="text-sm">Customer can view</span>
              </label>
            </div>
            <Field label="File (PDF, TXT, MD)" hint="PDF text is extracted automatically for Nick to search.">
              <button onClick={() => fileRef.current?.click()} className="w-full border border-dashed border-neutral-700 rounded-lg py-6 flex flex-col items-center gap-2 text-neutral-400 hover:border-[#73bf44] transition-colors" data-testid="document-file-picker">
                <UploadSimple size={22} />
                <span className="text-sm">{form.file ? form.file.name : "Click to choose a file"}</span>
              </button>
              <input ref={fileRef} type="file" accept=".pdf,.txt,.md,.csv" className="hidden" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <GhostButton onClick={() => setOpen(false)}>Cancel</GhostButton>
              <PrimaryButton onClick={submit} disabled={uploading} data-testid="document-submit">{uploading ? "Uploading..." : "Upload"}</PrimaryButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
        <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-white">Edit “{editRow?.title}”</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Title"><input value={editForm.title || ""} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className={inputCls} data-testid="edit-document-title" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category"><input value={editForm.category || ""} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} className={inputCls} placeholder="Electrical" /></Field>
              <Field label="Model year"><input value={editForm.applies_to_year || ""} onChange={(e) => setEditForm({ ...editForm, applies_to_year: e.target.value })} className={inputCls} placeholder="2026" /></Field>
            </div>
            <Field label="Applies to models" hint="Choose which caravan models can see this document.">
              <ModelAssign
                models={allModels}
                all={editAll}
                setAll={setEditAll}
                selected={editModels}
                setSelected={setEditModels}
                idp="edit-"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Visibility">
                <select value={editForm.visibility || "customer"} onChange={(e) => setEditForm({ ...editForm, visibility: e.target.value })} className={inputCls} data-testid="edit-document-visibility">
                  {VISIBILITY.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={editForm.status || "approved"} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className={inputCls} data-testid="edit-document-status">
                  <option value="draft">Draft</option><option value="approved">Approved</option><option value="archived">Archived</option>
                </select>
              </Field>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!editForm.ai_enabled} onChange={(e) => setEditForm({ ...editForm, ai_enabled: e.target.checked })} className="accent-[#73bf44] w-4 h-4" data-testid="edit-document-ai-enabled" />
                <span className="text-sm">AI can use</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!editForm.customer_enabled} onChange={(e) => setEditForm({ ...editForm, customer_enabled: e.target.checked })} className="accent-[#73bf44] w-4 h-4" data-testid="edit-document-customer-enabled" />
                <span className="text-sm">Customer can view</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <GhostButton onClick={() => setEditRow(null)}>Cancel</GhostButton>
              <PrimaryButton onClick={saveEdit} disabled={savingEdit} data-testid="edit-document-save">{savingEdit ? "Saving..." : "Save Changes"}</PrimaryButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Toggle({ on, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`w-10 h-5 rounded-full relative transition-colors ${on ? "bg-[#73bf44]" : "bg-neutral-700"}`}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}
